import { Injectable } from '@angular/core';
import { Observable, of, Subject, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { catchError } from 'rxjs/operators';

/**
 * RoleServiceConfiguration
 * Used as a configuration parameter when setting up RoleService
 */
export class RoleServiceConfiguration {
    environment: any; // Environment variables
    debug?: boolean;
    baseUri: string;
    productId: string;
}

@Injectable({
    providedIn: 'root'
})
export class ProductRoleService {

    public config: RoleServiceConfiguration;
    private _initialized$: Subject<boolean> = new Subject<boolean>();
    private _initialized: boolean = false;
    private _baseUri: string = '';
    private pendingChecks = Array<any>();

    constructor(private _http: HttpClient) {
        this._initialized$.subscribe((val) => {
            if (val === true) {
                this._initialized = true;
                this.pendingChecks.forEach(pending => {
                    this.makeCheckRequest(pending.message).subscribe((res) => pending.observable.next(res));
                });
            }
        });
    }

    /**
    * Check if current user has requested role
    * @param key role key
    * @returns boolean true if user has role
    */
    public userHasRole(role: string, product: string = this.config.productId): Observable<boolean> {
        const parameters = {
            product: product,
            role: role
        }
        if (this._initialized) {
            if (this[role] !== undefined) {
                return this[role];
            } else {
                this[role] = this.makeCheckRequest(parameters);
                return this[role];
            }
        } else {

            const obs = new Subject<boolean>();
            const pendingCheck = { message: parameters, observable: obs };

            this.pendingChecks.push(pendingCheck);
            return obs;
        }
    }

    /**
    * Initialize service with configuration variables
    */
    public init() {
        this.initialSetup();
    }

    /**
     * Actual setup method that should be ran after all required details are available
     */
    private initialSetup() {
        this._baseUri = `${this.config.baseUri}WebAPI/api`;
        this._initialized$.next(true);
    }

    private makeCheckRequest(message): Observable<boolean> {
        const url = `${this._baseUri}/User/IsProductRoleEnabled?productId=${message.product}&role=${message.role}`;
        return this._http.get<boolean>(url, { headers: this.createHeaders() })
            .pipe(catchError(this.handleError()));
    }

    /**
     * Generate headers for requests
     */
    private createHeaders(): HttpHeaders {
        const headers = new HttpHeaders();

        headers.append('Cache-Control', 'no-cache, no-store, must-revalidate');
        headers.append('Pragma', 'no-cache');
        headers.append('Expires', '0');

        return headers;
    }

    /**
     * Handling error statuscodes, mainly 400 bad request
     * that is returned when role cannot be resolved (case when Portal.BE version is not up to date)
     */
    private handleError() {
        return (err: any) => {
            if (err instanceof HttpErrorResponse && err.status === 400) {
                return of(false);
            }
            else {
                return throwError(err);
            }
        }
    }
}
